import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { Box, CircularProgress, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { EditIcon, ParametersIcon } from 'components'
import { useDeliveriesService } from 'services'
import { useGroupingLogic } from '../../hooks/useGroupingLogic'
import useGroupDeliveries from '../../dialogs/useGroupDeliveries'
import { handleDeliveryIcon } from '../consts'
import { StoredDeliveriesListDetailsItem } from './StoredDeliveriesListDetailsItem'
import {
  ButtonWrapper,
  DashedVerticalLine,
  GroupButton,
  HorizontalLine,
  EditIconContainer,
  Container,
  EmptyStateWrapper,
  DeliveryItemWrapper,
  LoaderContainer,
  ScrollContainer,
} from './StoredDeliveriesListDetails.styled'
import { Entity } from '../../../../types/permissions'
import { usePermissions } from '../../../../utils/hooks'

export const StoredDeliveriesListDetails = ({
  wheatCodeId,
  wheatCode,
  showDiscarded,
  showStorage,
}: {
  wheatCodeId: number
  wheatCode: string
  showDiscarded: boolean
  showStorage: boolean
}) => {
  const { checkPermission } = usePermissions()
  const navigate = useNavigate()
  const { getStoredDeliveriesThin, setGroupDeliveries, updateGroupDeliveries } =
    useDeliveriesService()
  const queryClient = useQueryClient()

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['fetch-stored-thin-deliveries', wheatCodeId, showDiscarded, showStorage],
      ({ pageParam = 0 }) =>
        getStoredDeliveriesThin(
          wheatCodeId,
          showDiscarded,
          showStorage,
          pageParam
        ),
      {
        getNextPageParam: (lastPage) => {
          const { offset, limit, total } = lastPage.pagination
          if (offset + limit < total) {
            return offset + limit
          }
          return undefined
        },
      }
    )

  const deliveries = useMemo(() => {
    return data?.pages.flatMap((page) => page.results) || []
  }, [data?.pages])

  const { groupedDeliveries, containerRef, lineStyles } = useGroupingLogic({
    id: wheatCodeId,
    delivery: deliveries,
    orientation: 'vertical',
  })

  const [openGroupModal, groupModal] = useGroupDeliveries({
    wheatCode: wheatCode,
    wheatCodeId: Number(wheatCodeId),
    onConfirm: async (groupedIds, groupId) => {
      if (groupId) {
        await updateGroupDeliveries(groupedIds, groupId)
      } else {
        await setGroupDeliveries(groupedIds)
      }

      await queryClient.invalidateQueries('fetch-stored-thin-deliveries')
      await queryClient.invalidateQueries('fetch-grouping-deliveries')
    },
  })

  const handleRedirectToStoredDeliveryDetails = (deliveryId: number) => {
    navigate(`/shipments/stored-deliveries/${deliveryId}/${wheatCodeId}`)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      if (
        container.scrollTop + container.clientHeight + 50 >=
        container.scrollHeight
      ) {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      }
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  if (isLoading) {
    return (
      <LoaderContainer>
        <CircularProgress size={24} color="primary" />
      </LoaderContainer>
    )
  }

  return (
    <Container>
      {deliveries.length < 1 && (
        <EmptyStateWrapper>
          <InfoOutlinedIcon fontSize="small" />
          <Typography fontSize="14px">No Deliveries</Typography>
        </EmptyStateWrapper>
      )}
      <ScrollContainer ref={containerRef}>
        {deliveries.map((deliveryDetail) => {
          const deliveryGroupId = deliveryDetail.delivery_group?.id
          const isGrouped =
            deliveryGroupId &&
            groupedDeliveries[deliveryGroupId]?.includes(deliveryDetail.id)
          const acceptedBin = deliveryDetail?.accepted_bin?.name
          const Icon = handleDeliveryIcon(deliveryDetail)

          return (
            <DeliveryItemWrapper key={deliveryDetail.id}>
              {isGrouped && (
                <HorizontalLine
                  data-divider-id={`divider-${wheatCodeId}-${deliveryDetail.id}`}
                />
              )}
              <StoredDeliveriesListDetailsItem
                date={deliveryDetail?.nir?.arrival_date}
                binName={acceptedBin}
                icon={Icon}
                onClick={() =>
                  handleRedirectToStoredDeliveryDetails(deliveryDetail?.id)
                }
              />
            </DeliveryItemWrapper>
          )
        })}

        {Object.entries(groupedDeliveries).map(
          ([groupId]) =>
            lineStyles[Number(groupId)] && (
              <Box key={groupId}>
                <DashedVerticalLine
                  orientation="vertical"
                  style={{
                    top: `${lineStyles[Number(groupId)].top}px`,
                    height: `${lineStyles[Number(groupId)].height}px`,
                  }}
                />
                <EditIconContainer
                  style={{
                    top: `${
                      Number(lineStyles[Number(groupId)].top) +
                      Number(lineStyles[Number(groupId)].height) / 2
                    }px`,
                    right: `20px`,
                  }}
                  onClick={() => openGroupModal(Number(groupId))}
                >
                  <EditIcon size={10} />
                </EditIconContainer>
              </Box>
            )
        )}
      </ScrollContainer>

      {deliveries.length > 1 && (
        <ButtonWrapper>
          <GroupButton
            variantType="secondary"
            size="small"
            startIcon={<ParametersIcon size={20} />}
            onClick={() => openGroupModal()}
            disabled={!checkPermission(Entity.DELIVERY, 'create_group')}
          >
            Group Deliveries
          </GroupButton>
        </ButtonWrapper>
      )}
      {groupModal}

      {isFetchingNextPage && (
        <LoaderContainer>
          <CircularProgress size={20} color="primary" />
        </LoaderContainer>
      )}
    </Container>
  )
}

export default StoredDeliveriesListDetails
